import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { ArrowRight } from "lucide-react";
import { fetchLedger, fetchStats, MARKET_LABELS } from "@/lib/api";
import SectionHeader from "@/components/SectionHeader";
import StatusBadge from "@/components/StatusBadge";
import { Skeleton } from "@/components/ui/skeleton";
import Odds from "@/components/Odds";

function StatBox({ label, value, sub, testid }) {
  return (
    <div className="card-mp p-5 sm:p-6" data-testid={testid}>
      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">{label}</p>
      <p className="mt-2 font-mono text-3xl font-bold tracking-tight text-[#00E676] sm:text-4xl">{value}</p>
      {sub && <p className="mt-1 font-mono text-[11px] text-slate-500">{sub}</p>}
    </div>
  );
}

export function HomeResults() {
  const { data: stats, isLoading: statsLoading } = useQuery({ queryKey: ["stats"], queryFn: fetchStats });
  const { data: ledger, isLoading: ledgerLoading } = useQuery({
    queryKey: ["ledger-home"],
    queryFn: () => fetchLedger({ limit: 6 }),
  });

  return (
    <section className="mx-auto max-w-7xl px-4 py-10 sm:px-6 sm:py-24 lg:px-8" data-testid="home-results-section">
      <SectionHeader
        eyebrow="Full Transparency"
        title="Track The Results. Not The Hype."
        sub="Every official Money Printer pick is recorded. Wins and losses stay visible — permanently."
        testid="home-results-header"
      />

      {statsLoading ? (
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-32 rounded-xl bg-[#141A26]" />)}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <StatBox label="Season Record" value={stats.record} sub={stats.demo ? "Demo data" : "Official record"} testid="stat-season-record" />
          <StatBox label="Net Units" value={`${stats.net_units > 0 ? "+" : ""}${stats.net_units}U`} sub="All tracked picks" testid="stat-net-units" />
          <StatBox label="Last 10" value={stats.last10} sub="Most recent settled" testid="stat-last-10" />
          <StatBox label="ROI" value={`${stats.roi > 0 ? "+" : ""}${stats.roi}%`} sub="Per unit staked" testid="stat-roi" />
        </div>
      )}

      <div className="card-mp mt-8 overflow-hidden" data-testid="recent-results-table">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] text-left text-sm">
            <thead>
              <tr className="border-b border-[#1E2738] font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                <th className="px-5 py-4">Date</th>
                <th className="px-5 py-4">Pick</th>
                <th className="px-5 py-4">Market</th>
                <th className="px-5 py-4">Odds</th>
                <th className="px-5 py-4">Units</th>
                <th className="px-5 py-4">Result</th>
                <th className="px-5 py-4 text-right">Profit/Loss</th>
              </tr>
            </thead>
            <tbody>
              {ledgerLoading
                ? [...Array(5)].map((_, i) => (
                    <tr key={i} className="border-b border-[#1E2738]/60">
                      <td colSpan={7} className="px-5 py-4"><Skeleton className="h-4 w-full bg-[#1E2738]" /></td>
                    </tr>
                  ))
                : (ledger?.rows || []).map((r) => (
                    <tr key={r.id} className="border-b border-[#1E2738]/60 transition-colors hover:bg-[#1A2234]/50" data-testid={`ledger-row-${r.id}`}>
                      <td className="px-5 py-4 font-mono text-xs text-slate-400">{dayjs(r.date).format("MMM D")}</td>
                      <td className="px-5 py-4 font-semibold text-white">{r.pick}</td>
                      <td className="px-5 py-4 text-slate-400">{MARKET_LABELS[r.market]}</td>
                      <td className="px-5 py-4 font-mono text-slate-300"><Odds value={r.odds} /></td>
                      <td className="px-5 py-4 font-mono text-slate-300">{r.units}U</td>
                      <td className="px-5 py-4"><StatusBadge status={r.result} /></td>
                      <td className={`px-5 py-4 text-right font-mono font-bold ${r.profit > 0 ? "text-[#00E676]" : r.profit < 0 ? "text-[#EF4444]" : "text-slate-400"}`}>
                        {r.profit > 0 ? `+${r.profit.toFixed(2)}U` : `${r.profit.toFixed(2)}U`}
                      </td>
                    </tr>
                  ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-8 flex flex-wrap items-center justify-between gap-4">
        <p className="font-mono text-[11px] uppercase tracking-wider text-slate-500" data-testid="home-results-note">
          {stats?.demo ? "Demo results shown — real ledger goes live at launch. Losses are never removed." : "Official Money Printer ledger. Losses are never removed."}
        </p>
        <Link to="/results" data-testid="view-full-results-btn" className="btn-outline !px-5 !py-2.5">
          View Full Results <ArrowRight size={15} />
        </Link>
      </div>
    </section>
  );
}

export default HomeResults;
